import { Box, Button, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, Card, CircularProgress, TableHead, Tooltip } from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import React, { useEffect, useState } from 'react'
import stylesheet from '../detail-container.stylesheet'
import EqualizerIcon from '@mui/icons-material/Equalizer'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import DeleteIcon from '@mui/icons-material/Delete'
import SyncIcon from '@mui/icons-material/Sync'
import EditIcon from '@mui/icons-material/Edit'
import InfoIcon from '@mui/icons-material/Info'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { PatientService } from '../../../../services/patient.service'
import { checkCondition } from '../common/condition-check'
import moment from 'moment'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import MenuIcon from '@mui/icons-material/Menu'
import EmptyCardComponent from '../common/empty-card.component'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import { RootState } from '../../../../redux/store'
import { useSelector } from 'react-redux'
import { environment } from '../../../../environments/environment'
import ChartRangeComponent from '../common/chart-range-component'
import { formatOptionLabel } from '../../../../utils/display-name-formatter'

interface IProps {
  input: any
  name: string
  outer: any
  patientId: string
  classes: ClassNameMap
}

const HistoryPDIComponent: React.FC<IProps> = ({ input, name, outer, patientId, classes }) => {
  const [data, setData] = useState([] as any)
  const [loading, setLoading] = useState(false)
  const [chart, setChart] = useState(false)
  const patientService = new PatientService()
  const [sortList, setSortList] = useState({
    sortBy: 'none',
    date: 'none',
    name: 'none',
  } as any)
  const [historyData, setHistoryData] = useState([] as any)
  const { updatePatientData } = useUpdateDataAction(patientId)
  const program_enrollments = useSelector((state: RootState) => state.patientDetail.patientDetail?.program_enrollments || [])

  useEffect(() => {
    setLoading(true)
    patientService
      .getMeasurementHistory(patientId, input.id)
      .then((res: any) => {
        setData(res.data.history)
        setHistoryData(sortData(res.data.history, 'table'))
        setLoading(false)
      })
      .catch((error) => {
        setLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      })
  }, [])

  useEffect(() => {
    if (sortList?.sortBy === 'date') {
      setHistoryData(
        sortList?.date === 'desc' ? [...historyData]?.sort((a: any, b: any) => b.date_recorded - a.date_recorded) : [...historyData]?.sort((a: any, b: any) => a.date_recorded - b.date_recorded)
      )
    }
  }, [sortList])

  const onCloseClick = (mode: string) => outer(input.id, mode)
  const onChartClick = () => setChart(!chart)
  const sortData = (d: any, type: string) => [...d]?.sort((a: any, b: any) => b.date_recorded - a.date_recorded)
  const chartData = sortData(data, 'chart')

  const getSortIcon = (d: any) => {
    if (d === 'desc') {
      return <ArrowDropDownIcon className={classes.programTargetEditIcon} />
    } else if (d === 'asc') {
      return <ArrowDropUpIcon className={classes.programTargetEditIcon} />
    } else {
      return <UnfoldMoreIcon className={classes.programTargetEditIcon} />
    }
  }

  const sortHistroyByType = (type: string) => {
    sortList[type] === 'asc' ? setSortList({ ...sortList, sortBy: type, [type]: 'desc' }) : setSortList({ ...sortList, sortBy: type, [type]: 'asc' })
  }

  const getTag = (d: any) => (d?.tag_detail ? <small>{` (${d?.tag_detail?.tag_display_name || d?.tag_detail?.tag_name})`}</small> : '')

  const syncDeviceData = async () => {
    setLoading(true)
    patientService
      .syncMeasurementData(patientId)
      .then((res: any) => {
        setLoading(false)
        const mData = res?.data || {}
        if (Object.keys(mData)?.length > 0) {
          if (mData?.message === 'No data imported') toast.warning('No data imported!', Helper.bottom_center())
          else if (mData?.id && mData?.message === 'success') {
            updatePatientData({ isMeasurement: true })
          }
        }
      })
      .catch((error) => {
        setLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      })
  }

  const columns = [
    { name: 'Date', width: '30%' },
    { name: 'Range', width: '20%' },
    { name: 'Value', width: '23%' },
    { name: 'Source', width: '15%' },
    { name: 'Actions', width: '12%' },
  ]

  const getValueRange = (val: number, range = '') => {
    return val && range === 'max' ? val + (val * 5) / 100 : val && range === 'min' ? val - (val * 5) / 100 : val
  }

  const getChartData = () => {
    let cData = [] as any
    const sortChart = [...data]?.sort((a: any, b: any) => a.date_recorded - b.date_recorded)?.filter((r: any) => r !== undefined)
    sortChart?.map((row: any) => {
      const val = parseFloat(row.value)
      if (!isNaN(val)) cData.push([moment.unix(row.date_recorded).format('YYYY-MM-DD'), val])
    })
    return cData
  }

  return (
    <Grid className={classes.customCard}>
      <Card variant="outlined">
        {!chart && (
          <Box className={classes.tableHistoryHeader} p={0.1}>
            <Typography className={classes.titleSmall}>
              <strong>{`${name} (${input.unit})`}</strong>
            </Typography>
          </Box>
        )}
        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label="history table" style={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {chart ? (
                  <TableCell className={classes.tableHistoryHeader} scope="row">
                    <Typography className={classes.titleSmall}>
                      <strong>{name}</strong>
                      {(input?.value_range?.min || input?.value_range?.max || input.unit) && (
                        <Typography component={'span'} pl={0.5} className={classes.subHeaderTitle} variant="subtitle2" color="textSecondary">
                          <small>
                            ({input?.value_range.min || ''}
                            {input?.value_range?.min && input?.value_range?.max ? '-' : ''}
                            {input?.value_range?.max}
                            {input?.value_range?.min && input?.value_range?.max ? ` ` : ''}
                            {input.unit})
                          </small>
                        </Typography>
                      )}
                    </Typography>
                  </TableCell>
                ) : (
                  <React.Fragment key={`target-device-header`}>
                    {!loading && (
                      <React.Fragment key={`target-device-header`}>
                        <TableCell className={classes.tablePtHistory} style={{ width: columns[0].width }} scope="row">
                          <Typography className={classes.titleSmall}>
                            {'Date'}
                            <IconButton size="small" disabled={historyData?.length < 1} onClick={() => sortHistroyByType('date')}>
                              {getSortIcon(sortList.date)}
                            </IconButton>
                            {name.includes('weight') && environment.okta.isEnabled && (
                              <IconButton size="small" onClick={() => syncDeviceData()} className={classes.expressModeBtn} sx={{ padding: '0px' }}>
                                <SyncIcon className={classes.infoIconFont} />
                                <strong>Sync Data</strong>
                              </IconButton>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.tableHeader} align="left" style={{ width: columns[1].width, cursor: 'pointer' }}>
                          {columns[1].name}
                        </TableCell>
                        <TableCell className={classes.tableHeader} align="left" style={{ width: columns[2].width }}>
                          {columns[2].name}
                        </TableCell>
                        <TableCell className={classes.tableHeader} align="center" style={{ width: columns[3].width }}>
                          {columns[3].name}
                        </TableCell>
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
                <TableCell className={classes.tableHeader} align="right" style={{ width: columns[4].width }}>
                  <IconButton disabled={historyData?.length < 1} size="small" onClick={() => onChartClick()}>
                    {chart ? <MenuIcon /> : <EqualizerIcon className={classes.ptHistoryIconSize} />}
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
          {loading ? (
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Box p={1}>
                <CircularProgress size="2rem" />
              </Box>
            </Grid>
          ) : (
            <Box>
              {checkCondition(
                historyData.length > 0,
                checkCondition(
                  chart,
                  <Box className={classes.historyData}>
                    <ChartRangeComponent
                      input={{
                        type: 'line',
                        data: getChartData(),
                        yMax: getValueRange(data[0]?.value_range?.max, 'max'),
                        yMin: getValueRange(data[0]?.value_range?.min, 'min'),
                      }}
                    />
                  </Box>,
                  <TableContainer component={Paper} className={classes.historyTableScroll}>
                    <Table className={classes.historyData} aria-label="simple table" style={{ tableLayout: 'fixed' }}>
                      <TableBody>
                        {historyData?.map((row: any, i: any) => (
                          <TableRow key={i}>
                            <TableCell className={classes.tablePaddingPT} scope="row" sx={{ width: columns[0].width }}>
                              <Typography color={'textSecondary'} className={classes.desc}>
                                <small>{moment.unix(row.date_recorded).format('MMM DD YYYY hh:mm a')}</small>
                              </Typography>
                            </TableCell>
                            <TableCell className={`${classes.tablePaddingPT} ${classes.desc}`} sx={{ width: columns[1].width }}>
                              <Typography color={'textSecondary'} className={classes.desc}>
                                ({row.value_range.min}
                                {row.value_range.min && row.value_range.max ? '-' : ''}
                                {row.value_range.max}
                                <small>
                                  {row.value_range.min || row.value_range.max ? ' ' : ''}
                                  {row.unit}
                                </small>
                                )
                              </Typography>
                            </TableCell>
                            <TableCell className={`${classes.tablePaddingPT}`} sx={{ width: columns[2].width }}>
                              <Typography color={'textSecondary'} className={classes.desc} textAlign={'left'}>
                                {formatOptionLabel(row.value)}
                                {getTag(row)}
                              </Typography>
                            </TableCell>
                            <TableCell
                              className={`${classes.tablePaddingPT}`}
                              align="center"
                              sx={{
                                width: columns[3].width,
                              }}
                            >
                              {row.source_value === 'Elation Clinical Document' ? (
                                <Tooltip title={row.evidence} arrow placement="bottom">
                                  <IconButton size="small" className={classes.infoIcon}>
                                    <InfoIcon className={classes.infoIconFont} />
                                  </IconButton>
                                </Tooltip>
                              ) : row.source_value === 'Lab PDF' ? (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const pdfUrl = `${environment.webservices.endpoint}${row.evidence}`
                                    window.open(pdfUrl, '_blank')
                                  }}
                                >
                                  <PictureAsPdfIcon
                                    sx={{
                                      fontSize: '0.8rem !important',
                                      color: 'error.main',
                                    }}
                                  />
                                </IconButton>
                              ) : (
                                <Typography
                                  color={'textSecondary'}
                                  className={classes.desc}
                                  sx={{
                                    textAlign: 'center',
                                  }}
                                >
                                  {row?.source_value || '-'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell className={classes.tableCellIconPtHistory} align="right" style={{ width: columns[4].width }}>
                              {input?.ref === 'measurements' && (
                                <>
                                  <IconButton
                                    className={classes.targetIcons}
                                    disabled={Helper.isPrgInActive(program_enrollments, input?.programs)}
                                    onClick={() =>
                                      outer(
                                        {
                                          id: input.id,
                                          historyData: {
                                            ...row,
                                            id: input.id,
                                            programs: input?.programs,
                                          },
                                        },
                                        'edit_history'
                                      )
                                    }
                                  >
                                    <EditIcon className={classes.infoIconFont} />
                                  </IconButton>
                                  <IconButton
                                    className={classes.targetIcons}
                                    disabled={Helper.isPrgInActive(program_enrollments, input?.programs)}
                                    onClick={() =>
                                      outer(
                                        {
                                          id: input.id,
                                          historyData: row,
                                        },
                                        'delete'
                                      )
                                    }
                                  >
                                    <DeleteIcon className={classes.ptHistoryIconSize} />
                                  </IconButton>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ),
                <Box p={2}>
                  <EmptyCardComponent />
                </Box>
              )}
            </Box>
          )}
          <Box className={classes.measurementButton} p={0.4}>
            <Grid container xs={12} direction="row" justifyContent="center" alignItems="center">
              <Typography>
                <Button onClick={() => onCloseClick('view')} size="small" className={classes.corfirmCancelButtonStyle}>
                  <strong>Close</strong>
                </Button>
              </Typography>
            </Grid>
          </Box>
        </TableContainer>
      </Card>
    </Grid>
  )
}

export default withStyles(stylesheet)(HistoryPDIComponent)
